import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { auth, functions, firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { formatHeight, formatWeight } from '../utils/heightWeightUtils';
import { getDisplayName, abbreviateDays, expandDays, getFirstName } from '../utils/flyerDisplay';
import { applyMerge, buildMergeDataForRecipient } from '../utils/emailMergeUtils';
import { validateMergeAgainstAllowlist } from '../utils/emailMergeTokens';
import { htmlToReadableText, plainTextToEmailHtml } from '../utils/emailHtmlUtils';
import { mergeFuryLabIntoFlyerForEmail, normalizeFuryLabConfigDoc } from '../utils/furyLabUtils';
import { getEventDateRangeWithYear, formatAfterPartyDateLabel } from '../utils/eventUtils';
import { FURY_LAB_TEMPLATES } from '../constants/furyLabEmailTemplates';
import EmailBodyEditor from './EmailBodyEditor';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import './EmailComposerModal.css';
import './admin/FmModal.css';

// Local HTML helpers (frontend-only; backend has its own).
function escapeHtml(s) {
  if (s == null) return '';
  const str = String(s);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Format event date for display (e.g. "March 21, 2026"). Handles ISO strings, Date, Firestore Timestamp. */
function formatEventDate(dateVal) {
  if (dateVal == null || String(dateVal).trim() === '') return null;
  let d;
  if (dateVal && typeof dateVal.toDate === 'function') {
    d = dateVal.toDate();
  } else if (dateVal instanceof Date) {
    d = dateVal;
  } else {
    const s = String(dateVal).trim();
    d = new Date(s);
  }
  if (Number.isNaN(d.getTime())) return null;
  // Force UTC so ISO-like values such as "2026-03-21" or midnight UTC timestamps
  // always render on the correct calendar date regardless of the admin's timezone.
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const flyerLabel = (f) => getDisplayName(f) || f.email || '—';

/** Build paragraph-based team members HTML for team introduction email (Freshlings + Mentor). */
const buildTeamIntroductionMembersHtml = (team, mentorsList, freshlingsList) => {
  if (!team) return '';
  const parts = [];
  const mentorFlyer =
    (team.mentor &&
      mentorsList &&
      mentorsList.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase())) ||
    null;
  const rawFreshlings = Array.isArray(team.freshlings) ? team.freshlings : [];
  const freshlingFlyers = rawFreshlings.map(
    (email) =>
      (freshlingsList &&
        freshlingsList.find((f) => (f.email || '').toLowerCase() === (email || '').toLowerCase())) || { email }
  );

  parts.push('<p style="margin:0 0 8px 0; font-size:14px; line-height:1.5;"><strong>Freshlings</strong></p>');
  freshlingFlyers.forEach((f, i) => {
    const name = flyerLabel(f);
    const email = (f.email || '').trim();
    const dz = escapeHtml(f.homeDz || '—');
    const jumps = f.jumpNumber != null ? String(f.jumpNumber) : '—';
    const ht = escapeHtml(formatHeight(f.ht) || '—');
    const wt = escapeHtml(formatWeight(f.wt) || '—');
    const attending = escapeHtml(expandDays(f.days) || abbreviateDays(f.days) || '—');
    const margin = i < freshlingFlyers.length - 1 ? '0 0 12px 0' : '0 0 16px 0';
    parts.push(
      `<p style="margin:${margin}; font-size:14px; line-height:1.5;">` +
        `<strong>${escapeHtml(name)}</strong><br/>` +
        `<a href="mailto:${escapeHtml(email)}" style="color:#1155cc;">${escapeHtml(email)}</a>` +
        '<br/>' +
        `${dz} &nbsp;|&nbsp; ${escapeHtml(jumps)} jumps &nbsp;|&nbsp; ` +
        `Attending: <strong>${attending}</strong> &nbsp;|&nbsp; ${ht} &nbsp;|&nbsp; ${wt}` +
        `</p>`
    );
  });

  parts.push('<p style="margin:0 0 6px 0; font-size:14px; line-height:1.5;"><strong>Mentor</strong></p>');
  if (mentorFlyer) {
    const name = flyerLabel(mentorFlyer);
    const email = (mentorFlyer.email || '').trim();
    parts.push(
      '<p style="margin:0 0 18px 0; font-size:14px; line-height:1.5;">' +
        `<strong>${escapeHtml(name)}</strong><br/>` +
        `<a href="mailto:${escapeHtml(email)}" style="color:#1155cc;">${escapeHtml(email)}</a>` +
        '</p>'
    );
  } else {
    parts.push('<p style="margin:0 0 18px 0; font-size:14px; line-height:1.5;">—</p>');
  }

  return parts.join('\n      ');
};

/** Build a greeting like "Hey Amanda, Chris, Zach!" using team members' first names. */
const buildTeamGreetingLine = (team, mentorsList, freshlingsList) => {
  if (!team) return 'Hey team,';

  const names = [];

  const rawFreshlings = Array.isArray(team.freshlings) ? team.freshlings : [];
  rawFreshlings.forEach((email) => {
    const flyer =
      (freshlingsList &&
        freshlingsList.find((f) => (f.email || '').toLowerCase() === (email || '').toLowerCase())) ||
      { email };
    const first = getFirstName(flyer) || flyerLabel(flyer);
    if (first) names.push(first);
  });

  if (team.mentor) {
    const mentorFlyer =
      (mentorsList &&
        mentorsList.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase())) ||
      { email: team.mentor };
    const first = getFirstName(mentorFlyer) || flyerLabel(mentorFlyer);
    if (first) names.push(first);
  }

  if (!names.length) return 'Hey team,';

  let nameList;
  if (names.length === 1) {
    nameList = names[0];
  } else if (names.length === 2) {
    nameList = `${names[0]} and ${names[1]}`;
  } else {
    nameList = `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }

  return `Hey ${nameList}!`;
};

function mergeTemplateValidationMessage(subject, bodyText, bodyHtml, preferHtml, allowlist) {
  if (!allowlist || !allowlist.length) return null;
  const body =
    preferHtml && bodyHtml && String(bodyHtml).trim() ? bodyHtml : bodyText || '';
  const v = validateMergeAgainstAllowlist(subject || '', body, allowlist);
  if (v.ok) return null;
  return `Disallowed merge tokens (update template or its allowlist in Email templates): ${v.invalidTokens.join(', ')}`;
}

const TEMPLATE_KEYS = {
  MENTOR_WARNING: 'mentor_warning',
  TEAM_INTRO: 'team_intro',
  EARLY_LOAD_WARNING: 'early_load_warning',
  MENTOR_LOAD_SUMMARY: 'mentor_load_summary',
  AFTER_PARTY_DESC_BUDGET: 'after_party_desc_budget',
  RECURRENCY_UPDATE: 'recurrency_update',
  RECURRENCY_CLASS_REMINDER: 'recurrency_class_reminder',
  FURY_LAB_INITIAL: 'fury_lab_initial',
  FURY_LAB_REMINDER: 'fury_lab_reminder',
  CUSTOM: 'custom',
};

/**
 * Template test: flyers rarely have Fury assignment fields; add preview values so
 * {{meetTime}}, {{blockSummary}}, {{coachName}} merge. Works for any template key (e.g. custom
 * Firestore id `fury_tunnel_lab_initial`), not only fury_lab_initial / fury_lab_reminder.
 */
function withTemplateTestFlyerPreviewDefaults(flyers) {
  const sampleBlock =
    'Sat 10:00-11:00 — Block 1 (preview sample)\nSat 11:15-12:15 — Block 2 (preview sample)';
  return (flyers || []).map((f) => ({
    ...f,
    meetTime:
      f.meetTime != null && String(f.meetTime).trim()
        ? String(f.meetTime).trim()
        : '9:00 AM (preview sample)',
    blockSummary:
      f.blockSummary != null && String(f.blockSummary).trim()
        ? String(f.blockSummary).trim()
        : sampleBlock,
    coachName:
      f.coachName != null && String(f.coachName).trim()
        ? String(f.coachName).trim()
        : 'Coach (preview sample)',
  }));
}

function buildTemplateTestMergeContext(baseCtx, testEvent, registrants) {
  const event = testEvent || baseCtx.event || null;
  const eventDateLabel =
    (event && formatEventDate(event.date)) ||
    (event && String(event.date || '').trim()) ||
    (baseCtx.eventDateLabel || '') ||
    '';
  return {
    ...baseCtx,
    event,
    eventDateLabel,
    registrants: withTemplateTestFlyerPreviewDefaults(registrants || []),
  };
}

function defaultSubjectForTemplate(templateKey, context) {
  switch (templateKey) {
    case TEMPLATE_KEYS.MENTOR_WARNING: {
      return 'You are a Fresh Meet Mentor! --Important Information--';
    }
    case TEMPLATE_KEYS.TEAM_INTRO: {
      return 'Your Fresh Meet Team Is Set!';
    }
    case TEMPLATE_KEYS.EARLY_LOAD_WARNING: {
      return 'Early load warning for Fresh Meet';
    }
    case TEMPLATE_KEYS.MENTOR_LOAD_SUMMARY: {
      return 'Fresh Meet – Tomorrow’s Details (Team Numbers + Planes)';
    }
    case TEMPLATE_KEYS.AFTER_PARTY_DESC_BUDGET: {
      return 'Fresh Meet After Party – description & budget';
    }
    case TEMPLATE_KEYS.RECURRENCY_UPDATE: {
      const flyer = context && (context.flyer || context.freshlingFlyer);
      const firstName = flyer ? getFirstName(flyer) : '';
      return firstName
        ? `Fresh Meet – do you still need a recurrency?`
        : 'Fresh Meet – do you still need a recurrency?';
    }
    case TEMPLATE_KEYS.RECURRENCY_CLASS_REMINDER: {
      const flyer = context && (context.flyer || context.freshlingFlyer);
      const firstName = flyer ? getFirstName(flyer) : '';
      return firstName
        ? `Fresh Meet recurrency reminder – your class time`
        : 'Fresh Meet recurrency reminder – your class time';
    }
    case TEMPLATE_KEYS.FURY_LAB_INITIAL: {
      return 'Fury Lab at Fresh Meet – Your time slots';
    }
    case TEMPLATE_KEYS.FURY_LAB_REMINDER: {
      return 'Reminder: Fury Lab at Fresh Meet – {{eventDate}}';
    }
    case TEMPLATE_KEYS.CUSTOM:
    default: {
      // If emailing a single flyer, default to a generic subject including their first name.
      const flyer = context && (context.flyer || context.mentorFlyer || context.freshlingFlyer);
      const firstName = flyer ? getFirstName(flyer) : '';
      return firstName ? `Fresh Meet message for ${firstName}` : 'Fresh Meet message';
    }
  }
}

/**
 * Reusable email composer modal for admin screens.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - context: {
 *     type?: 'mentor' | 'team' | 'flyer',
 *     team?: object,
 *     teamId?: string,
 *     mentors?: array,
 *     freshlings?: array,
 *     flyer?: object,
 *     mentorFlyer?: object,
 *     freshlingFlyer?: object,
 *     event?: object,
 *     markMentorWarningSent?: boolean,
 *     markTeamIntroSent?: boolean,
 *   }
 * - initialRecipients: { to: string, bcc: string }
 * - initialTemplate: 'mentor_warning' | 'team_intro' | 'custom'
 * - initialSubject?: string (e.g. for Fury Lab pre-filled template)
 * - initialBody?: string (plain text body when pre-filled)
 * - onEmailSent?: ({ selectedTemplate, context, to, bcc }) => void
 */
function EmailComposerModal({
  isOpen,
  onClose,
  context,
  initialRecipients,
  initialTemplate = TEMPLATE_KEYS.CUSTOM,
  initialSubject,
  initialBody,
  onEmailSent,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate || TEMPLATE_KEYS.CUSTOM);
  const [to, setTo] = useState((initialRecipients && initialRecipients.to) || '');
  const [bcc, setBcc] = useState((initialRecipients && initialRecipients.bcc) || '');
  const [subject, setSubject] = useState(defaultSubjectForTemplate(initialTemplate, context));
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyDirty, setBodyDirty] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testPickerOpen, setTestPickerOpen] = useState(false);
  const [testPickerIndex, setTestPickerIndex] = useState(0);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [sendMode, setSendMode] = useState('group'); // 'group' | 'individual'
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [testEvents, setTestEvents] = useState([]);
  const [testEventId, setTestEventId] = useState('');
  const [testFlyers, setTestFlyers] = useState([]);
  const [testViewAsFlyerIds, setTestViewAsFlyerIds] = useState([]);
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [libraryTemplateKey, setLibraryTemplateKey] = useState('');
  const [templateMergeAllowlist, setTemplateMergeAllowlist] = useState(null);
  const [testSelfEmail, setTestSelfEmail] = useState('');
  const [sendingTestSelf, setSendingTestSelf] = useState(false);
  const [emailBodyVisualSyncKey, setEmailBodyVisualSyncKey] = useState(0);

  // Capability flags for templates based on context.
  const canUseMentorTemplate = useMemo(() => {
    if (!context) return false;
    if (context.mentorFlyer || context.flyer) return true;
    if (context.team && (context.team.mentor || '').trim()) return true;
    return false;
  }, [context]);

  const canUseTeamTemplate = useMemo(() => {
    if (!context || !context.team) return false;
    const hasFreshlings = Array.isArray(context.team.freshlings) && context.team.freshlings.length > 0;
    return !!(hasFreshlings && context.mentors && context.freshlings);
  }, [context]);

  const canUseAfterPartyTemplate = useMemo(() => {
    if (!context) return false;
    // Enable for After Party ticket view and for template tests from the Templates tab.
    if (context.type === 'templateTest') return true;
    return context.subEventLabel === 'After Party';
  }, [context]);

  const canUseRecurrencyTemplate = useMemo(() => {
    if (!context) return false;
    const event = context.event;
    const flyer = context.flyer || context.freshlingFlyer;
    const bulk = context.mode === 'bulk';
    return !!(event && (flyer || bulk));
  }, [context]);

  const canUseFuryLabTemplate = useMemo(() => {
    if (context && context.type === 'templateTest') return true;
    return !!(context && context.furyLab);
  }, [context]);

  // Reset state when modal opens or context/template changes.
  useEffect(() => {
    if (!isOpen) return;
    const templateKey = initialTemplate || TEMPLATE_KEYS.CUSTOM;
    setSelectedTemplate(templateKey);
    setSendMode(
      context && context.furyLab
        ? 'individual'
        : context && context.defaultSendMode === 'individual'
          ? 'individual'
          : 'group'
    );
    setQueue([]);
    setQueueIndex(0);
    setTo((initialRecipients && initialRecipients.to) || '');
    setBcc((initialRecipients && initialRecipients.bcc) || '');
    setSubject(
      initialSubject != null && initialSubject !== ''
        ? initialSubject
        : defaultSubjectForTemplate(templateKey, context)
    );
    const isHtmlContext = !!(
      context &&
      (context.type === 'templateTest' || context.scope === 'sponsors')
    );
    const looksLikeHtml =
      isHtmlContext &&
      typeof initialBody === 'string' &&
      /<\/?[a-z][\s\S]*>/i.test(initialBody);
    if (looksLikeHtml) {
      // Show the exact HTML that will be sent in the Body box,
      // and also track it as HTML so it sends as HTML.
      setBody(initialBody);
      setBodyHtml(initialBody);
    } else {
      const bodyVal =
        initialBody != null && initialBody !== ''
          ? initialBody
          : '';
      setBody(bodyVal);
      setBodyHtml('');
    }
    setBodyDirty(false);
    setStatusMessage('');
    setTestEvents([]);
    setTestEventId((context && context.event && context.event.id) || '');
    setTestFlyers([]);
    setTestViewAsFlyerIds([]);
    setEmailBodyVisualSyncKey((k) => k + 1);
  }, [isOpen, initialTemplate, initialRecipients, initialSubject, initialBody, context]);

  useEffect(() => {
    const email = auth.currentUser?.email;
    if (email) {
      setTestSelfEmail((prev) => (prev && prev.trim() ? prev : email));
    }
  }, [isOpen]);

  const isTemplateTest = !!(context && context.type === 'templateTest');

  const testSelectedEvent = useMemo(() => {
    if (!isTemplateTest) return null;
    if (context && context.event && context.event.id && context.event.title) return context.event;
    return testEvents.find((e) => e.id === testEventId) || null;
  }, [isTemplateTest, context, testEvents, testEventId]);

  const effectiveContext = useMemo(() => {
    if (!context) return null;
    if (!isTemplateTest) return context;
    const registrants = testFlyers.filter((f) => testViewAsFlyerIds.includes(f.id));
    return buildTemplateTestMergeContext(
      context,
      testSelectedEvent || context.event || null,
      registrants
    );
  }, [context, isTemplateTest, testFlyers, testViewAsFlyerIds, testSelectedEvent]);

  // Load saved templates when in sponsors scope, so they can be applied from the preview.
  useEffect(() => {
    if (!isOpen || !context || context.scope !== 'sponsors') return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(firestore, 'emailTemplates'));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        list.sort((a, b) => (a.label || a.key || a.id || '').localeCompare(b.label || b.key || b.id || ''));
        setLibraryTemplates(list);
      } catch {
        // ignore; templates screen has its own error handling
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, context]);

  // Template test: load events and flyers for "View as" dropdown.
  useEffect(() => {
    if (!isOpen || !isTemplateTest) return;
    let cancelled = false;
    (async () => {
      try {
        const [evSnap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const evs = evSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTestEvents(evs);
        if (!testEventId && evs.length) {
          setTestEventId(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: evs })
          );
        }
      } catch (e) {
        // ignore; test mode only
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTemplateTest]);

  useEffect(() => {
    if (!isOpen || !isTemplateTest) return;
    if (!testEventId) return;
    let cancelled = false;
    (async () => {
      try {
        const [flySnap, configSnap, assignSnap] = await Promise.all([
          getDocs(query(collection(firestore, 'flyers'), where('eventId', '==', testEventId))),
          getDoc(doc(firestore, 'furyLabConfig', testEventId)),
          getDocs(
            query(collection(firestore, 'furyLabAssignments'), where('eventId', '==', testEventId))
          ),
        ]);
        if (cancelled) return;
        const list = flySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const filtered = list.filter((f) => f && (f.type === 'freshling' || f.type === 'mentor'));
        filtered.sort((a, b) =>
          (getDisplayName(a) || a.email || '').localeCompare(getDisplayName(b) || b.email || '', undefined, {
            sensitivity: 'base',
          })
        );
        let mergedList = filtered;
        if (configSnap.exists()) {
          const norm = normalizeFuryLabConfigDoc(configSnap.data() || {});
          if (norm) {
            const assignments = assignSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const bundle = { ...norm, assignments };
            mergedList = filtered.map((f) => mergeFuryLabIntoFlyerForEmail(f, bundle));
          }
        }
        setTestFlyers(mergedList);
        setTestViewAsFlyerIds([]);
      } catch (e) {
        if (!cancelled) {
          setTestFlyers([]);
          setTestViewAsFlyerIds([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isTemplateTest, testEventId]);

  // Load template content when template changes.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    async function loadTemplate() {
      setLoadingTemplate(true);
      setTemplateMergeAllowlist(null);
      try {
        // Try Firestore-backed templates first, but still run the context-specific
        // token replacement logic below (TEAM_MEMBERS_HTML, etc.).
        let dbTemplate = null;
        try {
          const dbKey =
            selectedTemplate && selectedTemplate !== TEMPLATE_KEYS.CUSTOM
              ? selectedTemplate
              : null;
          if (dbKey) {
            const ref = doc(firestore, 'emailTemplates', dbKey);
            const snap = await getDoc(ref);
            if (!cancelled && snap.exists()) {
              const data = snap.data() || {};
              dbTemplate = {
                format: data.format || 'html',
                subjectTemplate: data.subjectTemplate || '',
                bodyHtmlTemplate: data.bodyHtmlTemplate || '',
                bodyTextTemplate: data.bodyTextTemplate || '',
              };
              const am = Array.isArray(data.allowedMergeFields) ? data.allowedMergeFields : [];
              if (am.length > 0) {
                setTemplateMergeAllowlist(am);
              }
              if (
                dbTemplate.subjectTemplate &&
                (initialSubject == null || initialSubject === '')
              ) {
                setSubject(dbTemplate.subjectTemplate);
              }
            }
          }
        } catch (e) {
          // Swallow and fall back to legacy behavior.
          // eslint-disable-next-line no-console
          console.error('Failed to load email template from Firestore:', e);
        }

        if (selectedTemplate === TEMPLATE_KEYS.MENTOR_WARNING) {
          if (!canUseMentorTemplate) {
            if (!cancelled) {
              setBody('');
              setBodyHtml('');
            }
            return;
          }
          const baseUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
          const event = context && context.event;
          const eventDate =
            (event && event.date && (formatEventDate(event.date) || String(event.date).trim())) ||
            'the event date';

          // Resolve mentor flyer: explicit context.flyer/mentorFlyer, or from team.mentor + context.mentors
          let flyer = (context && (context.mentorFlyer || context.flyer)) || null;
          if (!flyer && context && context.team && context.mentors && (context.team.mentor || '').trim()) {
            const mentorEmail = (context.team.mentor || '').trim().toLowerCase();
            flyer = (context.mentors || []).find(
              (m) => (m.email || '').toString().trim().toLowerCase() === mentorEmail
            ) || null;
          }
          const mentorFirstName = flyer ? getFirstName(flyer) : '';
          const greetingName = mentorFirstName || 'there';

          let html = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!html) {
            const resp = await fetch(`${process.env.PUBLIC_URL || ''}/mentorWarningShot.html`);
            html = await resp.text();
          }
          if (cancelled) return;
          let out = html.replace(/\{\{DASHBOARD_BASE_URL\}\}/g, baseUrl);
          out = out.replace(/\{\{MENTOR_FIRST_NAME\}\}/g, greetingName);
          out = out.replace(/\{\{EVENT_DATE\}\}/g, eventDate);
          const eventWeekRangeLabel = getEventDateRangeWithYear(event) || 'the event week';
          out = out.replace(/\{\{EVENT_WEEK_RANGE\}\}/g, eventWeekRangeLabel);
          const text = htmlToReadableText(out);
          setBody(text);
          setBodyHtml('');
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.TEAM_INTRO) {
          if (!canUseTeamTemplate) {
            if (!cancelled) {
              setBody('');
              setBodyHtml('');
            }
            return;
          }
          let html = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!html) {
            const resp = await fetch(`${process.env.PUBLIC_URL || ''}/teamIntroduction.html`);
            html = await resp.text();
          }
          if (cancelled) return;
          const bodyMembersHtml = buildTeamIntroductionMembersHtml(
            context.team,
            context.mentors || [],
            context.freshlings || []
          );
          const greetingLine = buildTeamGreetingLine(
            context.team,
            context.mentors || [],
            context.freshlings || []
          );
          let fullHtml = html.replace(/\{\{TEAM_MEMBERS_HTML\}\}/g, bodyMembersHtml);
          fullHtml = fullHtml.replace(/\{\{TEAM_GREETING_LINE\}\}/g, greetingLine);
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.EARLY_LOAD_WARNING) {
          const defaultPlain =
            'Hello mentor! You are getting this email because your Fresh Meet team will likely be on the first or second plane, taking off at 8:30 on Saturday for the meet.\n\n' +
            'You should plan to meet early enough to register, get your dive, and prep. Registration opens at 7:00.\n\n' +
            'Emotionally prepare your Freshlings and make sure they understand the load will go with or without them. This may be their first experience with a scheduled skydiving load.\n\n' +
            'Thanks everyone!';
          let fullHtml = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!fullHtml) {
            const text =
              (dbTemplate && (dbTemplate.bodyTextTemplate || '').trim())
                ? dbTemplate.bodyTextTemplate
                : defaultPlain;
            fullHtml = plainTextToEmailHtml(text);
          }
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.MENTOR_LOAD_SUMMARY) {
          const rows = (context && Array.isArray(context.planeSummaryRows))
            ? context.planeSummaryRows
            : [];
          if (!rows.length) {
            if (!cancelled) {
              setBody('');
              setBodyHtml('');
            }
            return;
          }
          let html = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!html) {
            const resp = await fetch(`${process.env.PUBLIC_URL || ''}/mentorLoadSummary.html`);
            html = await resp.text();
          }
          if (cancelled) return;
          const regLink =
            (process.env.REACT_APP_FRESHMEET_REG_URL && process.env.REACT_APP_FRESHMEET_REG_URL.trim()) ||
            'https://freshmeet-d75a0.web.app/register';
          const rowsHtml = rows
            .map((r) => {
              const mentor = escapeHtml(r.mentorName || '');
              const teamNum = r.teamNumber ? `#${escapeHtml(String(r.teamNumber))}` : '—';
              const plane = escapeHtml(r.planeLabel || '');
              const takeoff = escapeHtml(r.takeoffLabel || '');
              return (
                `<div>[${mentor}] \u2013 Team ${teamNum} \u2013 ${plane} \u2013 ${takeoff}</div>`
              );
            })
            .join('');
          let fullHtml = html.replace(/\{\{TEAM_LINES_HTML\}\}/g, rowsHtml);
          fullHtml = fullHtml.replace(/\{\{REG_LINK\}\}/g, regLink);
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.AFTER_PARTY_DESC_BUDGET) {
          if (!canUseAfterPartyTemplate) {
            if (!cancelled) {
              setBody('');
              setBodyHtml('');
            }
            return;
          }
          let html = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!html) {
            const resp = await fetch(`${process.env.PUBLIC_URL || ''}/afterPartyDescriptionAndBudget.html`);
            html = await resp.text();
          }
          if (cancelled) return;
          const event = context && context.event;
          const mondayLabel =
            (event && formatAfterPartyDateLabel(event)) || 'the Monday after the meet';
          let fullHtml = html.replace(/\{\{AFTER_PARTY_DATE\}\}/g, mondayLabel);
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.RECURRENCY_UPDATE) {
          const baseUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
          let fullHtml = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!fullHtml) {
            const textBase =
              (dbTemplate && (dbTemplate.bodyTextTemplate || '').trim())
                ? dbTemplate.bodyTextTemplate
                : null;
            let text;
            if (textBase) {
              text = textBase.replace(/\{\{DASHBOARD_BASE_URL\}\}/g, baseUrl.replace(/\/$/, ''));
            } else {
              text =
                `Hi {{name}},\n\n` +
                `Do you still need a recurrency for Fresh Meet?\n\n` +
                `Please go here and update your status (even if you no longer need one):\n\n` +
                `{{DASHBOARD_BASE_URL}}/recurrency-update\n\n` +
                `If you do need a recurrency, the link will let you select a {{recurrencyWeekday}} time slot. You can also use that same link to change your status or adjust your time if your plans change.\n\n` +
                `If you are not able to make it on {{recurrencyDate}}, you'll need to book directly through Skydive Perris' skydiving school at (951) 657-1664.\n\n` +
                `Thanks! Looking forward to seeing you at Fresh Meet.`;
              text = text.replace(/\{\{DASHBOARD_BASE_URL\}\}/g, baseUrl.replace(/\/$/, ''));
            }
            fullHtml = plainTextToEmailHtml(text);
          }
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.RECURRENCY_CLASS_REMINDER) {
          const baseUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
          let fullHtml = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!fullHtml) {
            const textBase =
              (dbTemplate && (dbTemplate.bodyTextTemplate || '').trim())
                ? dbTemplate.bodyTextTemplate
                : null;
            let text;
            if (textBase) {
              text = textBase.replace(/\{\{DASHBOARD_BASE_URL\}\}/g, baseUrl.replace(/\/$/, ''));
            } else {
              text =
                `Hi {{firstName}},\n\n` +
                `Fresh Meet is almost here! This is a reminder about your scheduled recurrency next week.\n\n` +
                `**Your Check-In Time:** {{checkInTime}}\n` +
                `**Your Class Time:** {{classTime}}\n\n` +
                `Please arrive at your check-in time. This allows you to:\n\n` +
                `* Complete your waiver (if you haven't already)\n` +
                `* Add money to your account\n` +
                `* Get your gear organized\n\n` +
                `You are welcome to complete your waiver in advance here:\n` +
                `https://waiver.smartwaiver.com/w/uiwzl491x3de692rutlf/web/\n\n` +
                `The full event schedule can be found here:\n` +
                `https://freshmeet-d75a0.web.app/schedule\n\n` +
                `Perris' recurrency process may be more exhaustive than what you are used to at your home dropzone. We prioritize safety and training, especially for this event.\n\n` +
                `To help speed up the process, please review these questions in advance:\n` +
                `https://drive.google.com/file/d/1Nxsm5DxX1ZaSTWNhyQX4boOaJ07FbPGv/view?usp=sharing\n\n` +
                `You will need to answer these to the instructor's satisfaction before you can make your skydive.\n\n` +
                `The instructors are volunteers. You only need to cover your recurrency slot and your instructor's slot — there are no additional class or extra fees.\n\n` +
                `If your plans have changed and you need to update your status, please do so here:\n` +
                `{{recurrencyUpdateUrl}}\n\n` +
                `If you have any questions, let me know.\n\n` +
                `Looking forward to seeing you soon.\n\n` +
                `— Christy`;
              text = text.replace(/\{\{DASHBOARD_BASE_URL\}\}/g, baseUrl.replace(/\/$/, ''));
            }
            fullHtml = plainTextToEmailHtml(text);
          }
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else if (selectedTemplate === TEMPLATE_KEYS.FURY_LAB_INITIAL || selectedTemplate === TEMPLATE_KEYS.FURY_LAB_REMINDER) {
          const tmpl = FURY_LAB_TEMPLATES.find((t) => t.key === selectedTemplate);
          let fullHtml = (dbTemplate && (dbTemplate.bodyHtmlTemplate || '').trim())
            ? dbTemplate.bodyHtmlTemplate
            : '';
          if (!fullHtml) {
            const text =
              (dbTemplate && (dbTemplate.bodyTextTemplate || '').trim())
                ? dbTemplate.bodyTextTemplate
                : (tmpl ? tmpl.defaultBody : '');
            fullHtml = text ? plainTextToEmailHtml(text) : '';
          }
          setBodyHtml(fullHtml);
          setBody(htmlToReadableText(fullHtml));
          setBodyDirty(false);
        } else {
          // Custom template: empty body unless parent passed initialBody (e.g. Fury Lab).
          if (!cancelled) {
            if (initialBody == null || initialBody === '') {
              setBody('');
              setBodyHtml('');
            }
            setBodyDirty(false);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setBody('');
          setBodyHtml('');
          setStatusMessage(err.message || 'Failed to load template.');
        }
      } finally {
        if (!cancelled) {
          setLoadingTemplate(false);
          setEmailBodyVisualSyncKey((k) => k + 1);
        }
      }
    }

    loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedTemplate, canUseMentorTemplate, canUseTeamTemplate, canUseAfterPartyTemplate, context, initialBody, initialSubject]);

  const getUniqueRecipientEmails = () => {
    // In template-test mode, we may be previewing content "as" selected flyers while
    // delivering to a separate test inbox (the To field).
    if (isTemplateTest && testViewAsFlyerIds.length > 0) {
      const selected = testFlyers.filter((f) => testViewAsFlyerIds.includes(f.id));
      const emails = selected
        .map((f) => (f.email || '').toString().trim().toLowerCase())
        .filter(Boolean);
      return Array.from(new Set(emails));
    }
    const combined = [to || '', bcc || '']
      .join(',')
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    return Array.from(new Set(combined));
  };

  const buildIndividualQueue = () => {
    const emails = getUniqueRecipientEmails();
    if (!emails.length) return [];
    const subjectTrim = subject.trim();
    const useHtmlTemplate = !!(bodyHtml && !bodyDirty);
    const templateHtml = useHtmlTemplate ? bodyHtml : '';
    const templateText = body || '';
    const ctx = effectiveContext || context || {};
    const registrantsList = Array.isArray(ctx.registrants) ? ctx.registrants : [];
    return emails.map((emailAddr) => {
      let explicitFlyer = null;
      if (isTemplateTest && testViewAsFlyerIds.length > 0) {
        explicitFlyer = testFlyers.find((f) => (f.email || '').toString().trim().toLowerCase() === emailAddr) || null;
      } else if (registrantsList.length > 0) {
        const safeAddr = (emailAddr || '').toString().trim().toLowerCase();
        explicitFlyer = registrantsList.find((f) => f && (f.email || '').toString().trim().toLowerCase() === safeAddr) || null;
      }
      const mergeData = buildMergeDataForRecipient(ctx, emailAddr, explicitFlyer);
      const mergedSubject = applyMerge(subjectTrim, mergeData);
      const mergedHtml = templateHtml ? applyMerge(templateHtml, mergeData) : '';
      const mergedText = applyMerge(templateText, mergeData);
      return {
        email: emailAddr,
        subject: mergedSubject,
        bodyText: mergedText,
        bodyHtml: mergedHtml,
        useHtml: !!mergedHtml,
        attachmentUrls: Array.isArray(mergeData.sponsorAttachmentUrls) ? mergeData.sponsorAttachmentUrls : [],
        sponsorName: mergeData.sponsorName != null ? String(mergeData.sponsorName) : '',
        sponsorContactNames: mergeData.sponsorContactNames != null ? String(mergeData.sponsorContactNames) : '',
      };
    });
  };

  const canSendSponsorTest = useMemo(() => {
    return !!(context && context.scope === 'sponsors');
  }, [context]);

  const openTestPicker = () => {
    if (!canSendSponsorTest) return;
    const toTrim = to.trim();
    if (!toTrim) {
      setStatusMessage('Enter your email in To to send a test.');
      return;
    }
    let built = queue;
    if (sendMode === 'individual' && (!built || built.length === 0)) {
      built = buildIndividualQueue();
      setQueue(built);
      setQueueIndex(0);
      setSubject(built[0]?.subject || subject);
      setBody(built[0]?.bodyText || body);
      setBodyHtml(built[0]?.bodyHtml || '');
    }
    const list = sendMode === 'individual' ? (built || []) : [];
    if (!list.length) {
      setStatusMessage('No sponsor emails available to test. Click Preview emails first.');
      return;
    }
    setTestPickerIndex(Math.min(queueIndex || 0, list.length - 1));
    setTestPickerOpen(true);
  };

  const handleSendTestEmailForIndex = async (idx) => {
    if (!canSendSponsorTest) return;
    const toTrim = to.trim();
    if (!toTrim) {
      setStatusMessage('Enter your email in To to send a test.');
      return;
    }
    const item = queue && queue.length ? queue[idx] : null;
    const sendTeamEmail = httpsCallable(functions, 'sendTeamEmail');
    const subjectTrim = (item?.subject || subject || '').trim();
    const sendHtml = !!item?.useHtml;
    const messageToSend = (sendHtml ? item?.bodyHtml : item?.bodyText || body || '').trim();
    if (!subjectTrim) {
      setStatusMessage('Subject is required.');
      return;
    }
    if (!messageToSend) {
      setStatusMessage('Email body is empty.');
      return;
    }
    setSendingTest(true);
    setStatusMessage('Sending test email…');
    try {
      const res = await sendTeamEmail({
        to: toTrim,
        bcc: '',
        subject: `[TEST] ${subjectTrim}`,
        message: messageToSend,
        html: !!sendHtml,
        attachments: Array.isArray(item?.attachmentUrls) ? item.attachmentUrls.map((url) => ({ url })) : [],
      });
      const dbg = res && res.data && res.data.attachments ? res.data.attachments : null;
      const suffix = dbg
        ? ` (attachments requested ${dbg.requested || 0}, attached ${dbg.attached || 0}, skipped ${dbg.skipped || 0})`
        : '';
      setStatusMessage(`Test email sent successfully.${suffix}`);
      setTestPickerOpen(false);
    } catch (err) {
      setStatusMessage(err.message || 'Failed to send test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendTestToSelf = async () => {
    const addr = (testSelfEmail || '').trim();
    if (!addr) {
      setStatusMessage('Enter an address to receive test emails (defaults to your login email).');
      return;
    }
    const subjectTrim = subject.trim();
    if (!subjectTrim) {
      setStatusMessage('Subject is required.');
      return;
    }
    let sendHtml = false;
    let messageToSend = '';
    let attachments = [];
    if (sendMode === 'individual' && queue.length > 0) {
      const item = queue[queueIndex];
      sendHtml = !!item.useHtml;
      messageToSend = (sendHtml ? item.bodyHtml : item.bodyText || '').trim();
      attachments = Array.isArray(item.attachmentUrls)
        ? item.attachmentUrls.map((url) => ({ url }))
        : [];
    } else {
      sendHtml = !!(bodyHtml && !bodyDirty);
      messageToSend = (sendHtml ? bodyHtml : body || '').trim();
    }
    if (!messageToSend) {
      setStatusMessage('Email body is empty.');
      return;
    }
    setSendingTestSelf(true);
    setStatusMessage('Sending test email…');
    try {
      const sendTeamEmail = httpsCallable(functions, 'sendTeamEmail');
      await sendTeamEmail({
        to: addr,
        bcc: '',
        subject: `[TEST] ${subjectTrim}`,
        message: messageToSend,
        html: !!sendHtml,
        attachments,
      });
      setStatusMessage(`Test email sent to ${addr}.`);
    } catch (err) {
      setStatusMessage(err.message || 'Test send failed.');
    } finally {
      setSendingTestSelf(false);
    }
  };

  const handleSend = async () => {
    const toTrim = to.trim();
    const bccTrim = bcc.trim();
    if (!toTrim && !bccTrim) {
      setStatusMessage('At least one of To or BCC is required.');
      return;
    }
    const subjectTrim = subject.trim();
    if (!subjectTrim) {
      setStatusMessage('Subject is required.');
      return;
    }

    // Group mode: existing behavior.
    if (sendMode === 'group') {
      const sendHtml = !!(bodyHtml && !bodyDirty);
      const messageToSend = (sendHtml ? bodyHtml : body || '').trim();
      if (!messageToSend) {
        setStatusMessage('Email body is empty.');
        return;
      }

      const mergeErr = mergeTemplateValidationMessage(
        subjectTrim,
        body,
        bodyHtml,
        sendHtml,
        templateMergeAllowlist
      );
      if (mergeErr) {
        setStatusMessage(mergeErr);
        return;
      }

      setSending(true);
      setStatusMessage('');
      try {
        const sendTeamEmail = httpsCallable(functions, 'sendTeamEmail');
        await sendTeamEmail({
          to: toTrim,
          bcc: bccTrim,
          subject: subjectTrim,
          message: messageToSend,
          html: !!sendHtml,
        attachments: [],
        });

        // Audit log (global): record every send from the composer.
        try {
          const ctx = effectiveContext || context || {};
          const createdByUid = auth.currentUser?.uid || null;
          const finalRecipients = Array.from(
            new Set(
              [toTrim || '', bccTrim || '']
                .join(',')
                .split(',')
                .map((e) => e.trim().toLowerCase())
                .filter(Boolean)
            )
          );
          await addDoc(collection(firestore, 'emailSends'), {
            createdAt: serverTimestamp(),
            createdByUid,
            eventId: ctx.event?.id || null,
            contextType: ctx.type || null,
            subEventLabel: ctx.subEventLabel || null,
            templateKey: selectedTemplate || null,
            subject: subjectTrim || null,
            finalTo: toTrim || null,
            finalBcc: bccTrim || null,
            recipientCount: finalRecipients.length,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to write emailSends log:', e);
        }

        setStatusMessage('Email sent successfully.');
        // Simple confirmation, then close the modal.
        // (Status message is mostly for completeness if we ever keep the modal open.)
        // eslint-disable-next-line no-alert
        window.alert('Email sent successfully.');
        if (onEmailSent) {
          onEmailSent({
            selectedTemplate,
          context: effectiveContext || context,
            to: toTrim,
            bcc: bccTrim,
            subject: subjectTrim,
          });
        }
        if (onClose) {
          onClose();
        }
      } catch (err) {
        setStatusMessage(err.message || 'Failed to send email.');
      } finally {
        setSending(false);
      }
      return;
    }

    // Individual mode
    if (sendMode === 'individual' && !queue.length) {
      const useHtmlTemplatePre = !!(bodyHtml && !bodyDirty);
      const mergeErrPre = mergeTemplateValidationMessage(
        subjectTrim,
        body,
        bodyHtml,
        !!useHtmlTemplatePre,
        templateMergeAllowlist
      );
      if (mergeErrPre) {
        setStatusMessage(mergeErrPre);
        return;
      }
      const built = buildIndividualQueue();
      if (!built.length) {
        setStatusMessage('No valid recipients found for individual send.');
        return;
      }
      setQueue(built);
      setQueueIndex(0);
      setSubject(built[0].subject || subjectTrim);
      setBody(built[0].bodyText || body);
      setBodyHtml(built[0]?.bodyHtml || '');
      setStatusMessage(`Previewing 1 of ${built.length} personalized emails. Click Send again to send all.`);
      return;
    }

    if (sendMode === 'individual' && queue.length) {
      setSending(true);
      setStatusMessage('Sending personalized emails…');
      const sendTeamEmail = httpsCallable(functions, 'sendTeamEmail');
      const successes = [];
      const failures = [];
      for (let i = 0; i < queue.length; i += 1) {
        const item = queue[i];
        const mergeEmail = (item.email || '').trim();
        const deliveryTo = (isTemplateTest && testViewAsFlyerIds.length > 0 ? toTrim : mergeEmail).trim();
        if (!deliveryTo) {
          failures.push({ index: i, email: mergeEmail || '(empty)', error: 'Missing delivery email' });
          continue;
        }
        const itemSubject = (item.subject || subjectTrim).trim();
        const sendHtml = !!item.useHtml;
        const messageToSend = (sendHtml ? item.bodyHtml : item.bodyText || '').trim();
        if (!messageToSend) {
          failures.push({ index: i, email: mergeEmail, error: 'Empty body' });
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          await sendTeamEmail({
            to: deliveryTo,
            bcc: '',
            subject: itemSubject,
            message: messageToSend,
            html: !!sendHtml,
            attachments: Array.isArray(item.attachmentUrls)
              ? item.attachmentUrls.map((url) => ({ url }))
              : [],
          });
          successes.push(deliveryTo);
        } catch (err) {
          failures.push({ index: i, email: mergeEmail, error: err.message || 'Failed to send' });
        }
      }

      if (onEmailSent) {
        onEmailSent({
          selectedTemplate,
          context: effectiveContext || context,
          to: successes.join(','),
          bcc: '',
          subject: subjectTrim,
        });
      }

      // Audit log (global): record the send attempt (successes only).
      if (successes.length) {
        try {
          const ctx = effectiveContext || context || {};
          const createdByUid = auth.currentUser?.uid || null;
          await addDoc(collection(firestore, 'emailSends'), {
            createdAt: serverTimestamp(),
            createdByUid,
            eventId: ctx.event?.id || null,
            contextType: ctx.type || null,
            subEventLabel: ctx.subEventLabel || null,
            templateKey: selectedTemplate || null,
            subject: subjectTrim || null,
            finalTo: successes.join(',') || null,
            finalBcc: null,
            recipientCount: successes.length,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to write emailSends log:', e);
        }
      }

      if (failures.length === 0) {
        setStatusMessage('All personalized emails sent successfully.');
        // eslint-disable-next-line no-alert
        window.alert('All personalized emails sent successfully.');
        if (onClose) {
          onClose();
        }
      } else {
        const failedList = failures.map((f) => `${f.email} (${f.error})`).join('; ');
        setStatusMessage(`Some emails failed to send: ${failedList}`);
      }
      setSending(false);
    }
  };

  const handleTemplateChange = (e) => {
    const key = e.target.value;
    setSelectedTemplate(key);
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    setSubject(value);
    if (sendMode === 'individual' && queue.length) {
      setQueue((prev) =>
        prev.map((item, idx) =>
          idx === queueIndex ? { ...item, subject: value } : item
        )
      );
    }
  };

  const handleBodyChange = (e) => {
    const value = e.target.value;
    setBody(value);
    setBodyDirty(true);
    if (sendMode === 'individual' && queue.length) {
      setQueue((prev) =>
        prev.map((item, idx) =>
          idx === queueIndex
            ? { ...item, bodyText: value, useHtml: false }
            : item
        )
      );
    }
  };

  const handleHtmlBodyChange = (html) => {
    setBodyHtml(html);
    setBody(htmlToReadableText(html));
    setBodyDirty(true);
    if (sendMode === 'individual' && queue.length) {
      setQueue((prev) =>
        prev.map((item, idx) =>
          idx === queueIndex
            ? {
                ...item,
                bodyHtml: html,
                bodyText: htmlToReadableText(html),
                useHtml: true,
              }
            : item
        )
      );
    }
  };

  const previewAttachmentCount = useMemo(() => {
    if (sendMode === 'individual' && queue.length > 0) {
      const item = queue[queueIndex];
      return Array.isArray(item?.attachmentUrls) ? item.attachmentUrls.filter(Boolean).length : 0;
    }
    return 0;
  }, [sendMode, queue, queueIndex]);

  const showEmailBodyTabs = useMemo(() => {
    if (bodyHtml && /<\/?[a-z][\s\S]*>/i.test(bodyHtml)) return true;
    if (body && /<\/?[a-z][\s\S]*>/i.test(body)) return true;
    if (
      sendMode === 'individual' &&
      queue.length > 0 &&
      queue[queueIndex]?.useHtml &&
      (queue[queueIndex]?.bodyHtml || '').trim()
    ) {
      return true;
    }
    return false;
  }, [bodyHtml, body, sendMode, queue, queueIndex]);

  useModalEscapeKey(!!isOpen && testPickerOpen, () => setTestPickerOpen(false), { enabled: !sendingTest && !sending });
  useModalEscapeKey(!!isOpen && !testPickerOpen, () => {
    if (typeof onClose === 'function') onClose();
  });

  if (!isOpen) return null;

  const loadingLabel =
    selectedTemplate === TEMPLATE_KEYS.MENTOR_WARNING && loadingTemplate
      ? ' (loading mentor template…)'
      : selectedTemplate === TEMPLATE_KEYS.TEAM_INTRO && loadingTemplate
      ? ' (loading team template…)'
      : '';
  const recipientCount = getUniqueRecipientEmails().length;
  const sponsorPerRecipientMerge = !!(
    context &&
    context.sponsorMergeByEmail &&
    typeof context.sponsorMergeByEmail === 'object' &&
    Object.keys(context.sponsorMergeByEmail).length > 0
  );
  const canUseIndividualMode = recipientCount > 1 || sponsorPerRecipientMerge;
  const sendButtonLabel = sending
    ? 'Sending…'
    : sendMode === 'individual' && queue.length
      ? queue.length > 1
        ? 'Send all emails'
        : 'Send email'
      : sendMode === 'individual' && canUseIndividualMode
        ? 'Preview emails'
        : 'Send email';

  return (
    <div className="fm-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="fm-modal fm-modal--xl email-composer-modal-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-composer-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fm-modal-header">
          <h2 className="fm-modal-title" id="email-composer-modal-title">
            Email
            {context && context.team
              ? ` — Team ${context.team.teamNumber || 'Unassigned'}`
              : ''}
          </h2>
          <button type="button" className="fm-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="fm-modal-body email-composer-modal-body">
        <p className="team-pairing-notes-hint">
          Choose a template, adjust recipients, and edit the subject/body. Team introductions keep full
          HTML formatting unless you edit the body.
        </p>

        {(!context || context.scope !== 'sponsors') && (
          <div className="form-group">
            <label>
              Template{' '}
              <select value={selectedTemplate} onChange={handleTemplateChange}>
                <option value={TEMPLATE_KEYS.CUSTOM}>Custom</option>
                <option value={TEMPLATE_KEYS.RECURRENCY_UPDATE} disabled={!canUseRecurrencyTemplate}>
                  Recurrency update
                </option>
                <option value={TEMPLATE_KEYS.RECURRENCY_CLASS_REMINDER} disabled={!canUseRecurrencyTemplate}>
                  Recurrency class reminder
                </option>
                <option value={TEMPLATE_KEYS.MENTOR_WARNING} disabled={!canUseMentorTemplate}>
                  Mentor warning
                </option>
                <option value={TEMPLATE_KEYS.TEAM_INTRO} disabled={!canUseTeamTemplate}>
                  Team introduction
                </option>
                <option value={TEMPLATE_KEYS.AFTER_PARTY_DESC_BUDGET} disabled={!canUseAfterPartyTemplate}>
                  After party description &amp; budget
                </option>
                <option value={TEMPLATE_KEYS.EARLY_LOAD_WARNING}>
                  Early load warning
                </option>
                <option value={TEMPLATE_KEYS.MENTOR_LOAD_SUMMARY}>
                  Mentor load summary
                </option>
                <option value={TEMPLATE_KEYS.FURY_LAB_INITIAL} disabled={!canUseFuryLabTemplate}>
                  Fury Lab – Initial
                </option>
                <option value={TEMPLATE_KEYS.FURY_LAB_REMINDER} disabled={!canUseFuryLabTemplate}>
                  Fury Lab – Reminder
                </option>
              </select>
            </label>
          </div>
        )}

        {context && context.scope === 'sponsors' && (
          <div className="form-group">
            <label>
              Saved template
              <select
                value={libraryTemplateKey}
                onChange={(e) => {
                  const key = e.target.value;
                  setLibraryTemplateKey(key);
                  const tmpl = libraryTemplates.find((t) => (t.key || t.id) === key);
                  if (!tmpl) {
                    return;
                  }
                  const subj = tmpl.subjectTemplate || '';
                  const html = (tmpl.bodyHtmlTemplate || '').trim();
                  const text = tmpl.bodyTextTemplate || '';
                  setSubject((prev) => subj || prev);
                  if (html) {
                    setBodyHtml(html);
                    setBody(htmlToReadableText(html));
                  } else if ((text || '').trim()) {
                    const wrapped = plainTextToEmailHtml(text);
                    setBodyHtml(wrapped);
                    setBody(htmlToReadableText(wrapped));
                  } else {
                    setBody('');
                    setBodyHtml('');
                  }
                  setBodyDirty(false);
                  setQueue([]);
                  setQueueIndex(0);
                }}
              >
                <option value="">(none)</option>
                {libraryTemplates.map((t) => {
                  const key = t.key || t.id;
                  const label = t.label || key;
                  return (
                    <option key={key} value={key}>{label}</option>
                  );
                })}
              </select>
            </label>
          </div>
        )}

        {isTemplateTest && (
          <div className="form-group">
            <label>
              View as (sample data)
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '4px' }}>
                <label style={{ flex: 1 }}>
                  Event
                  <select
                    value={testEventId}
                    onChange={(e) => setTestEventId(e.target.value)}
                    disabled={sending}
                    style={{ width: '100%' }}
                  >
                    <option value="">(select)</option>
                    {testEvents.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title || ev.id}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </label>

            <label style={{ display: 'block', marginTop: '8px' }}>
              Flyers (multi-select; used for merge only)
              <select
                multiple
                size={6}
                value={testViewAsFlyerIds}
                onChange={(e) => {
                  const prevIds = testViewAsFlyerIds;
                  const ids = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setTestViewAsFlyerIds(ids);
                  // Selecting flyers should immediately rebuild preview content,
                  // but should NOT change the delivery address (To).
                  setSendMode('individual');
                  setQueue([]);
                  setQueueIndex(0);

                  if (!ids.length) return;
                  const selected = ids
                    .map((id) => testFlyers.find((f) => f.id === id))
                    .filter(Boolean);

                  // Special case: Team intro should preview a single team email composed from multiple people.
                  if (selectedTemplate === TEMPLATE_KEYS.TEAM_INTRO) {
                    const mentorCandidate = selected.find((f) => f.type === 'mentor') || selected[0];
                    const mentorEmailRaw = (mentorCandidate?.email || '').toString().trim();
                    const freshlingEmails = selected
                      .filter((f) => f !== mentorCandidate)
                      .map((f) => (f.email || '').toString().trim())
                      .filter(Boolean);
                    const team = {
                      mentor: mentorEmailRaw,
                      freshlings: freshlingEmails,
                      teamNumber: '',
                    };
                    const mentorsList = testFlyers.filter((f) => f.type === 'mentor');
                    const freshlingsList = testFlyers.filter((f) => f.type === 'freshling');
                    const ctx = {
                      ...(context || {}),
                      type: 'team',
                      event: testSelectedEvent || (context && context.event) || null,
                      team,
                      mentors: mentorsList,
                      freshlings: freshlingsList,
                    };

                    const subjectTrim = subject.trim();
                    const templateHtml = bodyHtml || '';
                    const bodyMembersHtml = buildTeamIntroductionMembersHtml(
                      team,
                      mentorsList,
                      freshlingsList
                    );
                    const greetingLine = buildTeamGreetingLine(
                      team,
                      mentorsList,
                      freshlingsList
                    );
                    let fullHtml = templateHtml;
                    fullHtml = fullHtml.replace(/\{\{TEAM_MEMBERS_HTML\}\}/g, bodyMembersHtml);
                    fullHtml = fullHtml.replace(/\{\{TEAM_GREETING_LINE\}\}/g, greetingLine);

                    const mentorEmailLower = mentorEmailRaw.toLowerCase();
                    const mentorFlyer =
                      mentorsList.find((m) => (m.email || '').toString().trim().toLowerCase() === mentorEmailLower) ||
                      null;
                    const mergeData = buildMergeDataForRecipient(ctx, mentorEmailRaw, mentorFlyer);
                    const mergedSubject = applyMerge(subjectTrim, mergeData);
                    const mergedHtml = applyMerge(fullHtml, mergeData);
                    const mergedText = htmlToReadableText(mergedHtml);
                    const built = [
                      {
                        email: mentorEmailLower || 'team',
                        subject: mergedSubject,
                        bodyText: mergedText,
                        bodyHtml: mergedHtml,
                        useHtml: true,
                      },
                    ];
                    setQueue(built);
                    setQueueIndex(0);
                    setSubject(built[0].subject || subject);
                    setBody(built[0].bodyText || body);
                    setEmailBodyVisualSyncKey((k) => k + 1);
                    return;
                  }

                  const emails = selected
                    .map((f) => (f.email || '').toString().trim().toLowerCase())
                    .filter(Boolean);
                  if (!emails.length) return;

                  const subjectTrim = subject.trim();
                  const useHtmlTemplate = !!(bodyHtml && !bodyDirty);
                  const templateHtml = useHtmlTemplate ? bodyHtml : '';
                  const templateText = body || '';

                  const ctx = buildTemplateTestMergeContext(
                    { ...(context || {}) },
                    testSelectedEvent || (context && context.event) || null,
                    selected
                  );

                  const built = emails.map((emailAddr) => {
                    const explicitFlyer =
                      selected.find(
                        (f) =>
                          (f.email || '').toString().trim().toLowerCase() === emailAddr
                      ) || null;
                    const mergeData = buildMergeDataForRecipient(ctx, emailAddr, explicitFlyer);
                    const mergedSubject = applyMerge(subjectTrim, mergeData);
                    const mergedHtml = templateHtml ? applyMerge(templateHtml, mergeData) : '';
                    const mergedText = applyMerge(templateText, mergeData);
                    return {
                      email: emailAddr,
                      subject: mergedSubject,
                      bodyText: mergedText,
                      bodyHtml: mergedHtml,
                      useHtml: !!mergedHtml,
                    };
                  });
                  if (!built.length) return;
                  // Prefer to show the last newly-selected person (so clicking different names updates the body).
                  const addedId = ids.find((id) => !prevIds.includes(id));
                  const activeId = addedId || ids[0];
                  const activeIdx = Math.max(0, ids.indexOf(activeId));
                  setQueue(built);
                  setQueueIndex(activeIdx);
                  const activeItem = built[activeIdx] || built[0];
                  setSubject(activeItem.subject || subject);
                  setBody(activeItem.bodyText || body);
                  setEmailBodyVisualSyncKey((k) => k + 1);
                }}
                disabled={!testFlyers.length || sending}
                style={{ width: '100%' }}
              >
                {testFlyers.map((f) => (
                  <option key={f.id} value={f.id}>
                    {(getDisplayName(f) || f.email || '—') + (f.email ? ` (${f.email})` : '')}
                  </option>
                ))}
              </select>
            </label>
            <p className="team-pairing-notes-hint" style={{ marginTop: '6px' }}>
              Select flyers to populate placeholders (like {'{{name}}'}). Emails will still be delivered to the address in “To”.
            </p>
          </div>
        )}

        {sponsorPerRecipientMerge && (
          <p className="team-pairing-notes-hint">
            Each address gets only that sponsor&apos;s prizes and winners. Use Preview emails, then Send to deliver.
            Placeholders include {'{{eventTitle}}'}, {'{{SponsorContactNames}}'}, {'{{SponsorCompanyName}}'} / {'{{sponsorName}}'},{' '}
            {'{{SponsorCompanyPossessive}}'}, {'{{sponsorType}}'},{' '}
            {'{{sponsorEventSnapshotHtml}}'}, {'{{sponsorFollowUpDetailsHtml}}'} (and individual stat tokens like{' '}
            {'{{freshlings}}'}, {'{{teams}}'}, …).
          </p>
        )}

        {canUseIndividualMode && (
          <div className="form-group email-composer-send-mode">
            <span className="email-composer-send-mode-label">Send mode</span>
            <div className="email-composer-send-mode-options">
              {!sponsorPerRecipientMerge && (
                <label className="email-composer-radio-option">
                  <input
                    type="radio"
                    name="send-mode"
                    value="group"
                    checked={sendMode === 'group'}
                    onChange={() => setSendMode('group')}
                    disabled={sending}
                  />
                  <span>One email (BCC all)</span>
                </label>
              )}
              <label className="email-composer-radio-option">
                <input
                  type="radio"
                  name="send-mode"
                  value="individual"
                  checked={sendMode === 'individual'}
                  onChange={() => {
                    setSendMode('individual');
                    setQueue([]);
                    setQueueIndex(0);
                  }}
                  disabled={sending}
                />
                <span>Individual emails (personalized)</span>
              </label>
            </div>
            {sendMode === 'individual' && !sponsorPerRecipientMerge && (
              <p className="team-pairing-notes-hint">
                {'You can use {{name}}, {{team}}, {{mentor}}, {{eventTitle}}, and {{subEventLabel}} in the subject and body.'}
              </p>
            )}
          </div>
        )}

        <div className="form-group">
          <label>
            {isTemplateTest && testViewAsFlyerIds.length > 0 ? 'Deliver to (test inbox)' : 'To'}
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="Comma-separated emails"
              disabled={sendMode === 'individual' && queue.length > 0}
            />
          </label>
        </div>

        <div className="form-group">
          <label>
            BCC
            <input
              type="text"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="Comma-separated emails"
              disabled={sendMode === 'individual' && queue.length > 0}
            />
          </label>
        </div>

        {sendMode === 'individual' && queue.length > 0 && (
          <div className="form-group">
            <span>
              Recipient {queueIndex + 1} of {queue.length}
              {queue[queueIndex]?.email ? (
                <>
                  {' '}
                  — <strong>{queue[queueIndex].email}</strong>
                </>
              ) : null}
            </span>
            <div style={{ marginTop: '4px' }}>
              <button
                type="button"
                onClick={() => {
                  if (queueIndex <= 0) return;
                  const nextIndex = queueIndex - 1;
                  setQueueIndex(nextIndex);
                  const item = queue[nextIndex];
                  setSubject(item.subject || subject);
                  setBody(item.bodyText || body);
                  setBodyHtml(item.bodyHtml || '');
                  setBodyDirty(false);
                  setEmailBodyVisualSyncKey((k) => k + 1);
                }}
                disabled={queueIndex === 0 || sending}
                style={{ marginRight: '0.5rem' }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => {
                  if (queueIndex >= queue.length - 1) return;
                  const nextIndex = queueIndex + 1;
                  setQueueIndex(nextIndex);
                  const item = queue[nextIndex];
                  setSubject(item.subject || subject);
                  setBody(item.bodyText || body);
                  setBodyHtml(item.bodyHtml || '');
                  setBodyDirty(false);
                  setEmailBodyVisualSyncKey((k) => k + 1);
                }}
                disabled={queueIndex >= queue.length - 1 || sending}
              >
                Next
              </button>
            </div>
            <p className="team-pairing-notes-hint" style={{ marginTop: '6px' }}>
              Review each recipient before sending all. Edit subject/body for this recipient only, then use Send when ready.
            </p>
          </div>
        )}

        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={handleSubjectChange}
          />
        </div>

        <div className="form-group">
          <label>
            Body
            {loadingLabel}
          </label>
          {showEmailBodyTabs ? (
            <>
              {bodyDirty && sendMode === 'individual' && queue.length > 0 && (
                <p className="team-pairing-notes-hint" style={{ marginBottom: '6px' }}>
                  Body was edited. Click &quot;Preview emails&quot; again (first Send click) to rebuild merged
                  messages for every recipient.
                </p>
              )}
              {sendMode === 'individual' && queue.length > 0 && (
                <p className="team-pairing-notes-hint" style={{ marginBottom: '6px' }}>
                  Attachments for this email: <strong>{previewAttachmentCount}</strong>
                </p>
              )}
              <EmailBodyEditor
                value={
                  sendMode === 'individual' && queue.length > 0 && queue[queueIndex]?.useHtml
                    ? queue[queueIndex].bodyHtml || ''
                    : bodyHtml && bodyHtml.trim()
                      ? bodyHtml
                      : body && /<\/?[a-z][\s\S]*>/i.test(body)
                        ? body
                        : bodyHtml || ''
                }
                contentSyncKey={emailBodyVisualSyncKey}
                onChange={handleHtmlBodyChange}
                disabled={loadingTemplate || sending}
                placeholder={
                  loadingTemplate
                    ? 'Loading template…'
                    : 'Write your message…'
                }
              />
            </>
          ) : (
            <textarea
              rows={8}
              value={body}
              onChange={handleBodyChange}
              placeholder={
                loadingTemplate
                  ? 'Loading template…'
                  : 'Write your message here…'
              }
            />
          )}
        </div>

        <p className="team-pairing-notes-hint email-composer-privacy-note">
          <strong>Recipients:</strong> In &quot;One email (BCC all)&quot; mode, each BCC address only sees their own
          email in the BCC field (not other recipients). Individual mode sends a separate message per person.
        </p>

        <div className="form-group email-composer-test-self">
          <label>
            Test send to
            <input
              type="email"
              value={testSelfEmail}
              onChange={(e) => setTestSelfEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={sending || sendingTestSelf}
            />
          </label>
          <button
            type="button"
            className="email-composer-test-self-btn"
            onClick={handleSendTestToSelf}
            disabled={sending || sendingTestSelf}
          >
            {sendingTestSelf ? 'Sending test…' : 'Send test to me'}
          </button>
        </div>

        {statusMessage && (
          <p
            className={
              statusMessage.startsWith('Email sent')
                ? 'team-pairing-email-success'
                : 'team-pairing-email-error'
            }
          >
            {statusMessage}
          </p>
        )}

        {testPickerOpen && canSendSponsorTest && (
          <div
            className="fm-modal-overlay email-composer-test-picker-overlay"
            onClick={() => setTestPickerOpen(false)}
            role="presentation"
          >
            <div
              className="fm-modal fm-modal--sm email-composer-test-picker"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="email-composer-test-picker-title"
            >
              <div className="fm-modal-header">
                <h2 className="fm-modal-title" id="email-composer-test-picker-title">
                  Send test email
                </h2>
                <button type="button" className="fm-modal-close" onClick={() => setTestPickerOpen(false)} aria-label="Close">
                  ✕
                </button>
              </div>
              <div className="fm-modal-body">
                <p className="team-pairing-notes-hint" style={{ marginTop: 0 }}>
                  Pick which sponsor&rsquo;s personalized email to deliver to <strong>{to.trim() || 'To'}</strong>.
                </p>
                <label style={{ display: 'block' }}>
                  Sponsor
                  <select
                    value={String(testPickerIndex)}
                    onChange={(e) => setTestPickerIndex(Math.max(0, Number(e.target.value) || 0))}
                    disabled={sendingTest || sending}
                    style={{ width: '100%', marginTop: '4px' }}
                  >
                    {(queue || []).map((item, i) => {
                      const sponsorLabel = (item?.sponsorName || '').trim() || item?.email || `Sponsor ${i + 1}`;
                      const people = (item?.sponsorContactNames || '').trim();
                      const label = people ? `${sponsorLabel} — ${people}` : sponsorLabel;
                      const suffix = item?.email ? ` (${item.email})` : '';
                      const attCount = Array.isArray(item?.attachmentUrls) ? item.attachmentUrls.filter(Boolean).length : 0;
                      return (
                        <option key={`${i}-${item?.email || 'row'}`} value={String(i)}>
                          {label + suffix + (attCount ? ` — ${attCount} attachment${attCount === 1 ? '' : 's'}` : '')}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>
              <div className="fm-modal-footer">
                <button
                  type="button"
                  className="fm-modal-btn fm-modal-btn-cancel"
                  onClick={() => setTestPickerOpen(false)}
                  disabled={sending || sendingTest}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="fm-modal-btn fm-modal-btn-primary"
                  onClick={() => {
                    const nextIdx = Math.max(0, Math.min(testPickerIndex, (queue?.length || 1) - 1));
                    setQueueIndex(nextIdx);
                    const it = queue[nextIdx];
                    setSubject(it?.subject || subject);
                    setBody(it?.bodyText || body);
                    setBodyHtml(it?.bodyHtml || '');
                    handleSendTestEmailForIndex(nextIdx);
                  }}
                  disabled={sending || sendingTest || !queue?.length}
                >
                  Send test
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
        <div className="fm-modal-footer email-composer-main-footer">
          <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="fm-modal-btn fm-modal-btn-cancel"
            onClick={() => {
              const text = `Subject: ${subject}\n\n${body}`;
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(text).catch(() => {});
              }
            }}
          >
            Copy to clipboard
          </button>
          {canSendSponsorTest && (
            <button
              type="button"
              className="fm-modal-btn fm-modal-btn-cancel"
              onClick={openTestPicker}
              disabled={sending || sendingTest}
              title="Sends the currently previewed sponsor email to the address in To (with winner photo attachments, if any)."
            >
              Send test to To
            </button>
          )}
          <button
            type="button"
            className="fm-modal-btn fm-modal-btn-primary"
            onClick={handleSend}
            disabled={sending}
          >
            {sendButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailComposerModal;

